// MediaRecorder wrapper used by STS Recording Booth. Handles getUserMedia +
// 1kHz preroll beeps + stream cleanup + post-stop head-trim for FB#27.
//
// The onstop guard (checking recorder is not null) matters because onstop
// fires async after our own close-path may have already nulled the recorder.
//
// Safari quirks worked around here:
//
//  * Beep reliability (FB#25, second pass): beeps play from a PRE-DECODED
//    AudioBuffer (`scheduleBeeps()` in `prerollBeep.ts`) instead of live
//    OscillatorNodes. Safari's oscillator scheduling has been observed to
//    drop scheduled events when the AudioContext sees ANY state churn
//    between `start(t)` and the event time — even on a shared,
//    already-running context. AudioBufferSourceNodes are battle-tested and
//    don't suffer the same drop pattern. The buffer is pre-rendered once
//    via OfflineAudioContext on `eagerInit()`, so by the time the user
//    clicks Record there's zero decode latency.
//
//  * First-0.5s drop (FB#27): MediaRecorder is started as soon as the mic
//    stream is acquired — typically while the visual countdown is still
//    running — so we capture audio across the boundary where the user begins
//    speaking. The recorded blob is then decoded and head-trimmed to the
//    requested moment ("go") via `trimAudioHead()` before being handed to
//    STS. We use `recordStartedAt` (a wall-clock perf.now() captured exactly
//    when MediaRecorder.start() returned) and the caller's `prerollEndsAt`
//    to compute the trim offset, with a 50ms click-suppression margin.

import { useCallback, useRef } from 'react';
import { audioBufferToWav } from '@/lib/audioExport';
import { isSafari } from '@/lib/audioContext';
import { scheduleBeeps, type BeepScheduleHandle } from '@/lib/prerollBeep';
import {
  canReuseStream,
  describeMicError,
  finishRecorder,
  headTrimSamples,
  isStreamLive,
  micConstraints,
  pickRecorderMime,
  toAudioInputs,
  type RecordingInterruption,
} from './recorderControl';

export type { RecordingInterruption } from './recorderControl';

export interface UseRecordingOptions {
  /** Eine laufende Aufnahme ist abgebrochen: Mikrofon getrennt (Track
   *  `ended`) oder der Recorder meldet einen Fehler. Nur während eine
   *  Aufnahme läuft — ein zwischen zwei Takes getrennter Stream wird beim
   *  nächsten `acquire()` still ersetzt (Audit M24). */
  onInterrupted?(reason: RecordingInterruption): void;
}

/** Obergrenze fürs Warten auf das `stop`-Event des MediaRecorders. */
const STOP_TIMEOUT_MS = 4000;

interface Internal {
  recorder: MediaRecorder | null;
  stream: MediaStream | null;
  chunks: Blob[];
  /** Handle for the currently-scheduled preroll beep batch — held so
   *  `cancelPreroll()` can stop the BufferSources WITHOUT closing the shared
   *  AudioContext (which would also kill take/version playback). */
  prerollHandle: BeepScheduleHandle | null;
  /** The deviceId the current `stream` was acquired with (or null for the
   *  browser default). Used by `acquire()` to decide whether the cached
   *  stream still matches the caller's request. */
  streamDeviceId: string | null;
  /** Performance.now() ms at which `recorder.start()` returned, or null when
   *  no recording is active. Surfaced by `stop()` so callers can compute
   *  the head-trim offset (FB#27). */
  recordStartedAt: number | null;
}

export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

export interface PrerollBeepOptions {
  beeps?: number;       // count, default 3
  intervalMs?: number;  // between-beep spacing, default 700
}

/** Result of `playPreroll`.
 *
 *  - `endsAt`: performance.now() ms at the visible "go" moment. Initially a
 *    sentinel far in the future (so the countdown freezes at the start digit
 *    until audio is actually scheduled); UPDATED to the real value once the
 *    AudioContext has resumed and the BufferSource is scheduled. The
 *    countdown should poll this on each tick to stay in lockstep with audio.
 *  - `go`: resolves at the visible "go" moment. Use this to transition out
 *    of countdown — `setTimeout(endsAt - perfNow)` would race the resume.
 *  - `settle`: resolves once scheduled beeps have played + nodes disconnected;
 *    callers `void` it without awaiting. */
export interface PrerollHandle {
  endsAt: number;
  go: Promise<void>;
  settle: Promise<void>;
}

/** Result of `stop()`. `recordStartedAt` lets the caller compute the head-trim
 *  offset relative to its preroll's `endsAt` (FB#27). */
export interface RecordingStopResult {
  blob: Blob;
  mime: string;
  recordStartedAt: number;
}

export function useRecording(options: UseRecordingOptions = {}) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const ref = useRef<Internal>({
    recorder: null,
    stream: null,
    chunks: [],
    prerollHandle: null,
    streamDeviceId: null,
    recordStartedAt: null,
  });

  /** Stop + disconnect any scheduled preroll BufferSources.
   *  Use to silence a stuck beep — Safari can replay scheduled events when an
   *  AudioContext suspends and resumes (e.g. after a window.confirm dialog
   *  dismisses), and an unfinished preroll can leak a stray beep that way.
   *
   *  We do NOT close the AudioContext here because it's the shared one used
   *  by take playback / version pills (`src/lib/audioContext.ts`). Cancelling
   *  the individual sources is sufficient — once `src.stop()` is called
   *  with a past time, the node won't fire even if the ctx later suspends
   *  and resumes. */
  const cancelPreroll = useCallback(() => {
    const handle = ref.current.prerollHandle;
    ref.current.prerollHandle = null;
    handle?.cancel();
  }, []);

  /** Schedule N short pre-rendered beeps SYNCHRONOUSLY (must be called from
   *  within a user gesture, e.g. the click handler that started recording).
   *  Returns immediately with a `PrerollHandle` whose `endsAt` is the wall-
   *  clock time of the "go" beat (the moment after the last beep) and whose
   *  `settle` promise resolves once playback is complete + the scheduled
   *  nodes are disconnected.
   *
   *  Beeps schedule pre-rendered AudioBufferSourceNodes on the SHARED
   *  AudioContext via `scheduleBeeps()` (see `prerollBeep.ts`). The buffer
   *  is rendered once at `eagerInit()` time, so this call has zero decode
   *  latency on the user-gesture path.
   *
   *  Why this matters (FB#25, second pass): the prior OscillatorNode-based
   *  implementation suffered intermittent dropped beeps on Safari even on
   *  the shared running context — Safari's oscillator scheduling drops
   *  events when the AudioContext sees state churn between `start(t)` and
   *  the event time. AudioBufferSourceNodes have a much smaller scheduling
   *  surface and don't exhibit the same drop pattern. */
  const playPreroll = useCallback(
    ({
      beeps = 3,
      intervalMs = 700,
    }: PrerollBeepOptions = {}): PrerollHandle => {
      // Defensive: cancel any leftover schedule before starting a new batch
      // (e.g. user clicks Record twice rapidly).
      const stale = ref.current.prerollHandle;
      if (stale) stale.cancel();
      ref.current.prerollHandle = null;

      // 0.3 linear gain matches the perceived loudness of the previous
      // oscillator-with-envelope path (which used 0.25 master * peak-1
      // envelope).
      const handle = scheduleBeeps({ beeps, intervalMs, gain: 0.3 });
      ref.current.prerollHandle = handle;

      // Clear our ref once the schedule's own settle resolves — but only if
      // we're still the active handle (a newer playPreroll may have stolen
      // the slot in the meantime).
      void handle.settle.then(() => {
        if (ref.current.prerollHandle === handle) {
          ref.current.prerollHandle = null;
        }
      });

      // Return the LIVE handle so callers polling .endsAt see post-resume
      // updates (the value starts at a sentinel + jumps to the real "go"
      // moment once audio is scheduled).
      return handle;
    },
    []
  );

  /** Acquire microphone permission + stream up-front. Triggers the browser's
   *  permission prompt immediately (so the user grants access BEFORE any
   *  countdown), without starting the MediaRecorder. The stream is held on
   *  the internal ref until `beginRecording()` consumes it (or `cancel()`
   *  releases it).
   *
   *  Idempotent per device: calling twice with the same `deviceId` (or twice
   *  with no deviceId) returns the cached stream. If a different deviceId
   *  is requested, the previous stream's tracks are stopped and a fresh
   *  stream is acquired so the recorder follows the caller's selection. */
  const acquire = useCallback(
    async (deviceId?: string | null): Promise<MediaStream> => {
      const wantId = deviceId ?? null;
      // Nur einen LEBENDEN Stream wiederverwenden: nach einem getrennten
      // Headset sind seine Tracks `ended` und bleiben es (Audit M24).
      if (ref.current.stream && canReuseStream(ref.current.stream, ref.current.streamDeviceId, wantId)) {
        return ref.current.stream;
      }
      // Mismatched or dead cached stream — tear down before re-acquiring.
      if (ref.current.stream) {
        ref.current.stream.getTracks().forEach((t) => t.stop());
        ref.current = { ...ref.current, stream: null, streamDeviceId: null };
      }
      const constraints = micConstraints(wantId);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        throw new Error(describeMicError(e), { cause: e });
      }
      ref.current = {
        ...ref.current,
        stream,
        streamDeviceId: wantId,
      };
      return stream;
    },
    [],
  );

  /** Enumerate the system's audio inputs. Labels are only populated AFTER
   *  the user has granted mic permission at least once in the session — call
   *  `acquire()` first if you need real labels. Devices with empty deviceIds
   *  (e.g. transient virtual devices on some platforms) are filtered out. */
  const enumerateInputs = useCallback(async (): Promise<AudioInputDevice[]> => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const all = await navigator.mediaDevices.enumerateDevices();
    return toAudioInputs(all);
  }, []);

  /** Create the MediaRecorder over the previously-acquired stream and start
   *  recording. Errors if `acquire()` hasn't been called yet. Returns the
   *  performance.now() ms at which `recorder.start()` ran — callers pair
   *  this with the preroll's `endsAt` to compute the post-stop trim offset.
   *
   *  Note (FB#27): callers should invoke this AS SOON AS the mic stream is
   *  available, even if the visual countdown is still running. Recording
   *  through the countdown window and trimming after stop is what guarantees
   *  the first phoneme of the user's "go" is captured — MediaRecorder takes
   *  a non-trivial amount of time to actually start emitting `dataavailable`
   *  chunks, especially on Safari, and starting it AT the "go" moment
   *  reliably drops the first ~0.3-0.5s. */
  const beginRecording = useCallback((): number => {
    const stream = ref.current.stream;
    if (!stream) {
      throw new Error('beginRecording() called without acquire()');
    }
    // Mime-preference order is browser-dependent. Safari 17.1+ shipped
    // MediaRecorder webm/opus support, so isTypeSupported('audio/webm;...')
    // returns true there now — but Safari's own decodeAudioData and the
    // MediaStream-tap audio bridge are battle-tested for mp4/AAC, not for
    // their freshly-added webm path. Result: opting into webm on Safari
    // gives us files that record fine but play back inconsistently
    // (silent take cards, occasional brummen on STS roundtrip). Force
    // mp4 first on Safari; keep webm-first for Chrome/Edge where it's
    // the native preferred container (pickRecorderMime).
    const mimeType = pickRecorderMime((m) => MediaRecorder.isTypeSupported(m), isSafari());
    if (!isStreamLive(stream)) {
      throw new Error(
        'The microphone was disconnected. Reconnect it and try again.',
      );
    }
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) ref.current.chunks.push(e.data);
    };
    // Abbruch mitten in der Aufnahme melden (Audit M24). Nur solange genau
    // dieser Recorder aktiv ist — `stop()`/`cancel()` setzen ihn auf null.
    const interrupt = (reason: RecordingInterruption) => {
      if (ref.current.recorder !== recorder) return;
      optionsRef.current.onInterrupted?.(reason);
    };
    recorder.onerror = () => interrupt('recorder-error');
    // Property statt addEventListener: der Stream lebt über mehrere Takes,
    // so ersetzt jede Aufnahme den Handler der vorigen.
    stream.getAudioTracks().forEach((t) => {
      t.onended = () => interrupt('device-lost');
    });
    // Pass a timeslice so MediaRecorder emits chunks every 100ms rather than
    // accumulating one giant chunk on stop(). Safari's MediaRecorder is
    // notoriously buggy without this — it sometimes only flushes a header
    // fragment without actual audio data on the second-and-later recording
    // within a session, producing a short malformed blob ElevenLabs rejects
    // as "File is corrupted".
    recorder.start(100);
    const recordStartedAt = performance.now();
    ref.current = {
      ...ref.current,
      recorder,
      stream,
      chunks: [],
      recordStartedAt,
    };
    return recordStartedAt;
  }, []);



  /** Convenience: acquire permission AND start recording in one call. Used
   *  by callers that don't need the up-front-permission separation. */
  const start = useCallback(
    async (deviceId?: string | null): Promise<number> => {
      await acquire(deviceId);
      return beginRecording();
    },
    [acquire, beginRecording],
  );

  // Läuft schon ein Stop (Klick + gleichzeitiger Geräteverlust), bekommt der
  // zweite Aufrufer dasselbe Ergebnis statt eines null.
  const stopInFlight = useRef<Promise<RecordingStopResult | null> | null>(null);
  const stop = useCallback((): Promise<RecordingStopResult | null> => {
    if (stopInFlight.current) return stopInFlight.current;
    const p = stopOnce().finally(() => { stopInFlight.current = null; });
    stopInFlight.current = p;
    return p;
  }, []);
  const stopOnce = async (): Promise<RecordingStopResult | null> => {
    const r = ref.current;
    const rec = r.recorder;
    const recordStartedAt = r.recordStartedAt;
    if (!rec || recordStartedAt == null) return null;
    // Tail-capture grace: hold the recorder open after the user clicks
    // Stop so the encoder pipeline finishes capturing the trailing word.
    // Chromium's MediaRecorder flushes reliably on stop() — no grace
    // needed there. Safari's encoder buffers 200-500ms internally and
    // sometimes drops the final chunk; symptom: "ein zwei drei → Stop"
    // lands with "drei" missing. 500ms grace covers the worst-case
    // observed buffer depth.
    //
    // finishRecorder löst IMMER auf — auch wenn der Browser den Recorder
    // nach einem getrennten Headset schon selbst gestoppt hat (vorher
    // hing das Promise, die Booth blieb in „generating", Audit M24).
    await finishRecorder(rec, {
      graceMs: isSafari() ? 500 : 0,
      timeoutMs: STOP_TIMEOUT_MS,
    });
    // Guard: cancel() may have cleared the recorder in the meantime.
    if (ref.current.recorder !== rec) return null;
    const chunks = ref.current.chunks;
    const mime = rec.mimeType || 'audio/webm';
    // Keep the mic stream ALIVE between takes (Safari fix). Tearing
    // down the stream + re-acquiring per take leaves Safari's audio
    // session in a degraded state — subsequent recordings come back
    // truncated/silent. The stream is only torn down on overlay
    // close via release()/cancel(). (A stream that died with the
    // headset is replaced by the next acquire().)
    ref.current = {
      recorder: null,
      stream: ref.current.stream,
      chunks: [],
      // Preserve any in-flight preroll bookkeeping — its `settle`
      // timer owns the cleanup, not us.
      prerollHandle: ref.current.prerollHandle,
      streamDeviceId: ref.current.streamDeviceId,
      recordStartedAt: null,
    };
    if (chunks.length === 0) return null;
    return { blob: new Blob(chunks, { type: mime }), mime, recordStartedAt };
  };

  const cancel = useCallback(() => {
    const r = ref.current;
    if (r.recorder) {
      r.recorder.onstop = null;
      r.recorder.onerror = null;
      r.recorder.ondataavailable = null;
      try { r.recorder.stop(); } catch { /* ignore */ }
    }
    r.stream?.getTracks().forEach((t) => t.stop());
    // Stop scheduled preroll sources, but DON'T close the shared
    // AudioContext — it powers take playback / version pills too.
    r.prerollHandle?.cancel();
    ref.current = {
      recorder: null,
      stream: null,
      chunks: [],
      prerollHandle: null,
      streamDeviceId: null,
      recordStartedAt: null,
    };
  }, []);

  /** Release the held mic stream + stop any pending preroll sources,
   *  but leave any in-flight recorder alone. Use this on overlay close when
   *  permission was acquired up-front but no recording was made — frees the
   *  mic LED so the user knows the page isn't listening. */
  const release = useCallback(() => {
    const r = ref.current;
    // If a recorder is in flight, leave it for `cancel()` / `stop()`.
    if (r.recorder) return;
    r.stream?.getTracks().forEach((t) => t.stop());
    r.prerollHandle?.cancel();
    ref.current = {
      recorder: null,
      stream: null,
      chunks: [],
      prerollHandle: null,
      streamDeviceId: null,
      recordStartedAt: null,
    };
  }, []);

  return {
    playPreroll,
    cancelPreroll,
    acquire,
    enumerateInputs,
    beginRecording,
    start,
    stop,
    cancel,
    release,
  };
}

/** Decode `blob` (any browser-decodable audio: webm/opus, m4a, mp3, etc.),
 *  drop the first `headSeconds` of samples, and re-encode the remainder as a
 *  16-bit PCM WAV. Returns the trimmed Blob with mime `audio/wav`.
 *
 *  Used by the STS Recording Booth (FB#27) to remove the silent countdown
 *  prefix from a take that was recorded through the countdown window — see
 *  this file's header for the rationale.
 *
 *  WAV is the right output format here because:
 *    1. STS API accepts arbitrary audio mimes — no server-side change needed.
 *    2. We've already paid the decode-to-PCM cost; re-encoding to MP3/Opus
 *       in-browser would add a lossy step and require an external encoder.
 *    3. The recording is short (<60s typical), so WAV size is fine over HTTP.
 *
 *  Edge cases:
 *    - If `headSeconds <= 0` (recorder started after countdown ended): no
 *      trim, decode-then-WAV roundtrip still happens for format normalisation.
 *    - If `headSeconds >= buffer.duration` (shouldn't happen — would mean we
 *      recorded ONLY countdown): returns a 1-sample WAV. STS will reject and
 *      the user retries; this avoids an empty-Blob NPE downstream. */
export async function trimAudioHead(
  blob: Blob,
  headSeconds: number,
): Promise<Blob> {
  // Decode using a one-shot AudioContext. Closed immediately after decode so
  // we don't pile up contexts on repeated takes.
  const ac = new AudioContext();
  let decoded: AudioBuffer;
  try {
    const arr = await blob.arrayBuffer();
    // decodeAudioData detaches the buffer on some implementations — slice() to
    // pass a copy so the original ArrayBuffer stays usable for the caller.
    decoded = await ac.decodeAudioData(arr.slice(0));
  } finally {
    try { void ac.close(); } catch { /* ignore */ }
  }

  const sr = decoded.sampleRate;
  const numCh = decoded.numberOfChannels;
  const headSamples = headTrimSamples(headSeconds, sr, decoded.length);
  const remaining = decoded.length - headSamples;
  if (remaining <= 0) {
    // Degenerate: trim consumed the whole buffer. Return a tiny 1-sample WAV
    // so downstream code that expects a non-empty blob doesn't NPE on
    // duration probes; STS will reject and the user retries.
    const empty = new AudioBuffer({
      length: 1,
      numberOfChannels: numCh,
      sampleRate: sr,
    });
    return audioBufferToWav(empty);
  }

  const trimmed = new AudioBuffer({
    length: remaining,
    numberOfChannels: numCh,
    sampleRate: sr,
  });
  for (let c = 0; c < numCh; c++) {
    const src = decoded.getChannelData(c);
    const dst = trimmed.getChannelData(c);
    dst.set(src.subarray(headSamples, headSamples + remaining));
  }
  return audioBufferToWav(trimmed);
}
